import { useRef, useState } from "react";
import { Bell, Camera, KeyRound, Save, User as UserIcon } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { api, errMsg, NOTIF_TYPES } from "../lib/api";
import { PageHeader, UserAvatar } from "../components/ui-bits";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";

export default function Profile() {
  const { user, setUser } = useAuth();
  const [name, setName] = useState(user.name);
  const [email, setEmail] = useState(user.email);
  const [notify, setNotify] = useState(user.notify_settings || {});
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const saveProfile = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/auth/profile", { name, email, notify_settings: notify });
      setUser(data.user);
      toast.success("Profil gespeichert");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const changePassword = async () => {
    if (pw.next.length < 8) return toast.error("Neues Passwort: mindestens 8 Zeichen");
    if (pw.next !== pw.confirm) return toast.error("Passwörter stimmen nicht überein");
    try {
      await api.post("/auth/change-password", { current_password: pw.current, new_password: pw.next });
      toast.success("Passwort geändert");
      setPw({ current: "", next: "", confirm: "" });
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const uploadAvatar = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/auth/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUser({ ...user, avatar: data.avatar });
      toast.success("Profilbild aktualisiert");
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <div className="animate-fade-up" data-testid="profile-page">
      <PageHeader overline="Konto" title="Profil" testid="profile-header" />

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <section className="rounded-2xl border border-[#1D225C]/10 bg-white p-6 shadow-sm" data-testid="profile-info">
          <h3 className="mb-4 flex items-center gap-2 font-display text-base font-semibold text-[#1D225C]">
            <UserIcon className="h-4 w-4 text-[#B88A4A]" /> Persönliche Daten
          </h3>
          <div className="mb-5 flex items-center gap-4">
            <UserAvatar name={user.name} color={user.color} avatar={user.avatar} size={16} testid="profile-avatar" />
            <div>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadAvatar} data-testid="profile-avatar-input" />
              <Button variant="outline" size="sm" data-testid="profile-avatar-button" onClick={() => fileRef.current?.click()} className="rounded-lg">
                <Camera className="mr-1.5 h-3.5 w-3.5" /> Bild ändern
              </Button>
              <p className="mt-1.5 text-xs text-[#777]">{user.title}</p>
            </div>
          </div>
          <div className="space-y-4">
            <div>
              <Label>Name</Label>
              <Input data-testid="profile-name-input" value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5" />
            </div>
            <div>
              <Label>E-Mail</Label>
              <Input data-testid="profile-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5" />
            </div>
            <Button data-testid="profile-save-button" onClick={saveProfile} disabled={saving} className="rounded-xl bg-[#1D225C] text-white hover:bg-[#2a3070]">
              <Save className="mr-1.5 h-4 w-4" /> {saving ? "Speichern…" : "Speichern"}
            </Button>
          </div>
        </section>

        <div className="space-y-5">
          <section className="rounded-2xl border border-[#1D225C]/10 bg-white p-6 shadow-sm" data-testid="profile-password">
            <h3 className="mb-4 flex items-center gap-2 font-display text-base font-semibold text-[#1D225C]">
              <KeyRound className="h-4 w-4 text-[#B88A4A]" /> Passwort ändern
            </h3>
            <div className="space-y-4">
              <div>
                <Label>Aktuelles Passwort</Label>
                <Input data-testid="profile-pw-current" type="password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} className="mt-1.5" autoComplete="current-password" />
              </div>
              <div>
                <Label>Neues Passwort</Label>
                <Input data-testid="profile-pw-new" type="password" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} className="mt-1.5" autoComplete="new-password" />
              </div>
              <div>
                <Label>Neues Passwort bestätigen</Label>
                <Input data-testid="profile-pw-confirm" type="password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} className="mt-1.5" autoComplete="new-password" />
              </div>
              <Button data-testid="profile-pw-submit" onClick={changePassword} variant="outline" className="rounded-xl">
                Passwort ändern
              </Button>
            </div>
          </section>

          <section className="rounded-2xl border border-[#1D225C]/10 bg-white p-6 shadow-sm" data-testid="profile-notifications">
            <h3 className="mb-4 flex items-center gap-2 font-display text-base font-semibold text-[#1D225C]">
              <Bell className="h-4 w-4 text-[#B88A4A]" /> Benachrichtigungen
            </h3>
            <div className="space-y-3">
              {NOTIF_TYPES.map((t) => (
                <label key={t.key} className="flex items-center justify-between gap-4">
                  <span className="text-sm text-[#1D225C]">{t.label}</span>
                  <Switch
                    data-testid={`profile-notify-${t.key}`}
                    checked={notify[t.key] !== false}
                    onCheckedChange={(v) => setNotify((n) => ({ ...n, [t.key]: v }))}
                  />
                </label>
              ))}
              <p className="text-xs text-[#999]">Änderungen werden mit „Speichern" übernommen.</p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
